import { FluentProvider } from "@fluentui/react-components";
import { createContext, type ReactNode, useContext } from "react";
import { type ColourSchemePreference, useColourScheme } from "./useColourScheme";

type ColourScheme = {
  isDark: boolean;
  preference: ColourSchemePreference;
  choose: (next: ColourSchemePreference) => void;
};

const ColourSchemeContext = createContext<ColourScheme | null>(null);

/**
 * Holds the colour scheme once, for the whole app.
 *
 * This is a provider rather than a hook called where it is needed, and that is the whole point:
 * `useColourScheme` keeps its choice in React state, so calling it in two places produced two
 * independent states. `FluentProvider` had one and the theme button had the other, which meant
 * pressing the button wrote the preference to storage, re-rendered its own label, and left the
 * actual theme alone until the next full page load. Nothing failed; the button simply did not
 * work, which is a harder bug to notice than one that throws.
 */
export function ColourSchemeProvider({ children }: { children: ReactNode }) {
  const { theme, isDark, preference, choose } = useColourScheme();

  return (
    <ColourSchemeContext.Provider value={{ isDark, preference, choose }}>
      <FluentProvider theme={theme} style={{ minHeight: "100vh" }}>
        {children}
      </FluentProvider>
    </ColourSchemeContext.Provider>
  );
}

export function useScheme(): ColourScheme {
  const scheme = useContext(ColourSchemeContext);

  if (!scheme) {
    throw new Error("useScheme needs a ColourSchemeProvider above it.");
  }

  return scheme;
}
